import React, { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Speciality } from '../types';

interface SpecialitiesSectionProps {
  specialities: Speciality[];
}

const AUTO_SCROLL_INTERVAL = 3000; // ms
const SCROLL_FACTOR = 0.9; // % of container width

const SpecialitiesSection: React.FC<SpecialitiesSectionProps> = ({ specialities }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * SCROLL_FACTOR;

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const startAutoScroll = () => {
    stopAutoScroll();

    autoScrollRef.current = setInterval(() => {
      if (!scrollRef.current) return;

      const container = scrollRef.current;
      const isAtEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 10;

      container.scrollTo({
        left: isAtEnd ? 0 : container.scrollLeft + container.clientWidth,
        behavior: 'smooth',
      });
    }, AUTO_SCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  useEffect(() => {
    startAutoScroll();
    return stopAutoScroll;
  }, []);

  return (
    <section className="py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-blue-800 dark:text-blue-400 font-black text-[9px] uppercase tracking-[0.4em] mb-2">
              Our Expertise
            </h2>
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white uppercase tracking-tighter leading-none mb-4">
              Infrastructure <span className="text-blue-600">Specialities</span>
            </h3>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-3 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
              aria-label="Scroll Left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-3 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
              aria-label="Scroll Right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Auto Scroll Container */}
        <div
          ref={scrollRef}
          onMouseEnter={stopAutoScroll}
          onMouseLeave={startAutoScroll}
          className="flex gap-6 overflow-x-auto no-scrollbar pb-8 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {specialities.map((spec) => (
            <div
              key={spec.id}
              className="flex-shrink-0 w-[300px] md:w-[400px] snap-start group"
            >
              <div className="relative h-[250px] md:h-[300px] rounded-3xl overflow-hidden mb-6 shadow-lg">
                <img
                  src={spec.imageUrl}
                  alt={spec.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
              </div>

              <h4 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2 group-hover:text-blue-700 transition-colors">
                {spec.title}
              </h4>
              <p className="text-slate-500 leading-relaxed font-medium line-clamp-2">
                {spec.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpecialitiesSection;